import {
  ActivityDetails,
  ActivityUpdate,
  ActivitySummary,
  AddLinkRequest,
  AddWatcherRequest,
  ApprovalCreate,
  CompletionCriterion,
  CompletionCriterionDetails,
  Link,
  ProjectsService,
  ActivityDocument,
  Document,
  ActivityCreateRequest,
  UpdateActivityDocumentRequest,
  Action,
  ActivityTagCreate,
  ActivityProjectDuplicateRequest,
  ActionDetails,
  ActivitiesUpdateRequest,
  ActivitiesFavoriteUpdateRequest,
  ActivitiesTagsUpdateRequest,
  TagTemplate,
  Comment,
  CommentCreateRequest,
  CommentUpdateRequest,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useActivityProjectRulesStore } from '@/store/activityProjectRulesStore';
import { useChecklistStore } from '@/store/checklistStore';
import { useProjectStore } from '@/store/projectStore';
import { plusXDaysString } from '@/utils/helpers/datetime';
import {
  isConnectionViolatedByDeadline,
  isRelationViolatedByDeadline,
} from '@/utils/helpers/violatedEdges';

type ProjectId = string;

export interface EnrichedProjectActivityDocument {
  id: string;
  activityDocument: ActivityDocument;
  document: Document;
  inputActivity: ActivitySummary | null;
}

interface ActivityProjectStoreState {
  projects: Record<string, ActivityDetails[]>;
  activities: Record<ProjectId, ActivityDetails[]>;
}

/** The project summary's entry of an activity, from its details. */
function activitySummaryOf(activity: ActivityDetails): ActivitySummary {
  return {
    ...structuredClone(toRaw(activity)),
    approvers: (activity.approvals ?? [])
      .map((approval) => approval.approver.function_id)
      .filter((fnId): fnId is string => !!fnId),
  };
}

export const useActivityProjectStore = defineStore('activityProjectStore', {
  state: (): ActivityProjectStoreState => ({
    projects: {},
    activities: {},
  }),

  getters: {
    activity() {
      return (projectId: string, activityId: string) => {
        const project = this.projects[projectId];
        if (!project) {
          throw Error('project does not exist');
        }
        const activity = project.find(({ id }) => id === activityId);
        if (!activity) {
          throw Error('activity does not exist');
        }

        return activity;
      };
    },

    actions() {
      return (projectId: string, activityId: string) => {
        if (!this.hasActivity(projectId, activityId)) {
          return [];
        }
        const actions = this.activity(projectId, activityId).actions;

        return actions || [];
      };
    },

    hasActivity() {
      return (projectId: string, activityId: string) => {
        const project = this.projects[projectId];
        if (!project) {
          return false;
        }

        return project.some(({ id }) => id === activityId);
      };
    },

    responsibleUser() {
      return (projectId: string, activityId: string) => {
        const activity = this.activity(projectId, activityId);
        const responsibleId = activity?.responsible.user?.id;

        return responsibleId || null;
      };
    },

    predecessors() {
      return (projectId: string, activityId: string) => {
        if (!this.hasActivity(projectId, activityId)) {
          return [];
        }
        const activity = this.activity(projectId, activityId);

        return activity.predecessors
          .map((predecessorId) =>
            useProjectStore().activitySummary(projectId, predecessorId),
          )
          .sort((a, b) => (a.number > b.number ? 1 : -1))
          .map((p) => p.id);
      };
    },

    successors() {
      return (projectId: string, activityId: string): string[] => {
        if (!this.hasActivity(projectId, activityId)) {
          return [];
        }
        const activity = this.activity(projectId, activityId);

        return activity.successors
          .map((successorId) =>
            useProjectStore().activitySummary(projectId, successorId),
          )
          .sort((a, b) => (a.number > b.number ? 1 : -1))
          .map((p) => p.id);
      };
    },

    potentialPredecessors() {
      return (projectId: string, activityId: string) => {
        const project = useProjectStore().project(projectId);
        const activity = this.activity(projectId, activityId);
        if (!activity) {
          return [];
        }

        const currentPhaseIndex = project.phases.findIndex(
          ({ id }) => id === activity.phase,
        );
        const activitiesInFormerPhases: ActivitySummary[] = project.phases
          .filter((_, index) => index < currentPhaseIndex)
          .map((phase) => {
            return project.activities.filter((a) => a.phase === phase.id);
          })
          .flat();

        const activitiesInSamePhase = project.activities.filter(
          ({ id, current_deadline, phase }) =>
            phase === activity.phase &&
            id !== activityId &&
            current_deadline <= activity.current_deadline,
        );
        const predecessors = [
          ...activitiesInFormerPhases,
          ...activitiesInSamePhase,
        ];

        const existingPredecessorIds = activity.predecessors.map((pId) => pId);

        return predecessors.filter(
          ({ id }) => !existingPredecessorIds.includes(id),
        );
      };
    },

    availableSuccessors() {
      return (projectId: string, activityId: string) => {
        const project = useProjectStore().project(projectId);
        const activity = this.activity(projectId, activityId);
        if (!activity) {
          return [];
        }

        return project.activities.filter(
          ({ id, current_deadline }) =>
            id !== activity.id &&
            !activity.successors.includes(id) &&
            current_deadline >= activity.current_deadline,
        );
      };
    },

    links() {
      return (projectId: string, activityId: string) => {
        if (!this.hasActivity(projectId, activityId)) {
          return [];
        }
        const projectStore = useProjectStore();
        const activity = this.activity(projectId, activityId);

        return activity.links
          .map((id) =>
            projectStore.hasLink(projectId, id)
              ? projectStore.link(projectId, id)
              : null,
          )
          .filter((l) => l) as Link[];
      };
    },

    link() {
      return (projectId: string, activityId: string, linkId: string) => {
        const links = this.links(projectId, activityId);
        const link = links.find((l) => l.id === linkId);
        if (!link) {
          throw new Error(`Missing link ${linkId} in project`);
        }

        return link;
      };
    },

    enrichedActivityDocuments() {
      return (
        projectId: string,
        activityId: string,
      ): EnrichedProjectActivityDocument[] => {
        const projectStore = useProjectStore();
        const project = projectStore.project(projectId);
        const activity = this.activity(projectId, activityId);
        if (!activity || !project) {
          return [];
        }

        return activity.documents.map((activityDocument) => {
          const document = project.documents.find(
            (doc) => doc.id === activityDocument.id,
          );

          if (!document) {
            throw Error(
              `Missing document ${activityDocument.id} from the activity store`,
            );
          }

          let inputActivity: ActivitySummary | null = null;
          if (activityDocument.input) {
            inputActivity = projectStore.activitySummary(
              projectId,
              activityDocument.input,
            );
          }

          return {
            id: document.id,
            activityDocument,
            document,
            inputActivity,
          };
        });
      };
    },

    completionCriteria() {
      return (projectId: string, activityId: string) => {
        if (!this.hasActivity(projectId, activityId)) {
          return [];
        }
        const activity = this.activity(projectId, activityId);

        return activity.completion_criteria;
      };
    },

    criterion() {
      return (
        projectId: string,
        activityId: string,
        criterionId: string,
      ): CompletionCriterion => {
        const activity = this.activity(projectId, activityId);
        const cc = activity.completion_criteria.find(
          ({ id }) => id === criterionId,
        );
        if (!cc) {
          throw new Error('Missing completion criterion in activity');
        }

        return cc;
      };
    },

    action() {
      return (
        projectId: string,
        activityId: string,
        actionId: string,
      ): Action => {
        const activity = this.activity(projectId, activityId);
        const action = activity.actions.find(({ id }) => id === actionId);
        if (!action) {
          throw new Error('Missing action in activity');
        }

        return action;
      };
    },

    ccOrActionMinDeadline() {
      return (projectId: string) => {
        const project = useProjectStore().project(projectId);
        const [startDate] = project.start_date.split('T');

        return startDate;
      };
    },

    ccOrActionMaxDeadline() {
      return (projectId: string, activityId: string) => {
        const activity = this.hasActivity(projectId, activityId)
          ? this.activity(projectId, activityId)
          : useProjectStore().activitySummary(projectId, activityId);

        const date = new Date(activity.current_deadline);
        date.setUTCDate(date.getUTCDate() - 1);
        const [endDate] = date.toISOString().split('T');

        return endDate;
      };
    },

    lastPredecessorDeadline() {
      return (projectId: string, activityId: string) => {
        const activityDetails = this.hasActivity(projectId, activityId)
          ? this.activity(projectId, activityId)
          : null;

        if (!activityDetails?.predecessors) {
          return undefined;
        }

        // only currently-valid relations bound the window: a kept
        // violated predecessor (already after this activity) does not clamp
        return activityDetails.predecessors
          .map(
            (predecessor: string) =>
              useProjectStore().activitySummary(projectId, predecessor)
                .current_deadline,
          )
          .filter(
            (deadline) =>
              !isRelationViolatedByDeadline(
                deadline,
                activityDetails.current_deadline,
              ),
          )
          .sort()
          .at(-1);
      };
    },

    latestPhase() {
      return (projectId: string) => {
        const project = useProjectStore().project(projectId);

        return project.phases[project.phases.length - 1];
      };
    },

    previousUnplannedOrDonePhaseDate() {
      return (projectId: string, activityId: string) => {
        const project = useProjectStore().project(projectId);
        const activity = this.hasActivity(projectId, activityId)
          ? this.activity(projectId, activityId)
          : useProjectStore().activitySummary(projectId, activityId);

        return project.phases
          .filter(
            (phase) =>
              ((phase.state === 'unplanned' &&
                activity.state !== 'unplanned') ||
                phase.state === 'done') &&
              activity.current_deadline.split('T')[0] >
                phase.deadline.split('T')[0],
          )
          .map((phase) => phase.deadline)
          .sort()
          .at(-1);
      };
    },

    activityMinDeadline() {
      return (projectId: string, activityId: string) => {
        const project = useProjectStore().project(projectId);
        const dates = [plusXDaysString(project.start_date, 1)];

        const lastPredecessorDeadline = this.lastPredecessorDeadline(
          projectId,
          activityId,
        );

        if (lastPredecessorDeadline) {
          dates.push(lastPredecessorDeadline);
        }

        const previousUnplannedOrDonePhaseDate =
          this.previousUnplannedOrDonePhaseDate(projectId, activityId);
        if (previousUnplannedOrDonePhaseDate) {
          dates.push(plusXDaysString(previousUnplannedOrDonePhaseDate, 1));
        }

        return dates.sort().at(-1);
      };
    },

    requiredMilestone() {
      return (projectId: string, activityId: string) => {
        const activity = this.hasActivity(projectId, activityId)
          ? this.activity(projectId, activityId)
          : useProjectStore().activitySummary(projectId, activityId);

        if (!activity.milestone?.required) {
          return undefined;
        }
        const milestoneId = activity.milestone.milestone;
        const milestone = useProjectStore().milestone(projectId, milestoneId);
        const milestonePhase = useProjectStore()
          .project(projectId)
          .phases.find(
            (phase) =>
              phase.final_milestone.id === milestoneId ||
              phase.intermediate_milestones.some((m) => m.id === milestoneId),
          );
        // a kept violated connection does not clamp the picker
        const violated =
          !!milestonePhase &&
          isConnectionViolatedByDeadline(
            { phase: activity.phase, deadline: activity.current_deadline },
            { phase: milestonePhase.id, deadline: milestone.current_deadline },
          );

        return violated ? undefined : milestone;
      };
    },

    firstSuccessorDeadline() {
      return (projectId: string, activityId: string) => {
        const activityDetails = this.hasActivity(projectId, activityId)
          ? this.activity(projectId, activityId)
          : null;

        if (!activityDetails?.successors) {
          return undefined;
        }

        // only currently-valid relations bound the window
        return activityDetails.successors
          .map(
            (successor: string) =>
              useProjectStore().activitySummary(projectId, successor)
                .current_deadline,
          )
          .filter(
            (deadline) =>
              !isRelationViolatedByDeadline(
                activityDetails.current_deadline,
                deadline,
              ),
          )
          .sort()[0];
      };
    },

    nextUnplannedOrDonePhase() {
      return (projectId: string, activityId: string) => {
        const project = useProjectStore().project(projectId);
        const activity = this.hasActivity(projectId, activityId)
          ? this.activity(projectId, activityId)
          : useProjectStore().activitySummary(projectId, activityId);

        return project.phases
          .filter(
            (phase) =>
              ((phase.state === 'unplanned' &&
                activity.state !== 'unplanned') ||
                phase.state === 'done') &&
              activity.current_deadline.split('T')[0] <
                phase.deadline.split('T')[0],
          )
          .map((phase) => useProjectStore().phaseStart(projectId, phase.id))
          .sort()[0];
      };
    },

    activityMaxDeadline() {
      return (projectId: string, activityId: string) => {
        const dates = [this.latestPhase(projectId).deadline];

        const requiredMilestone = this.requiredMilestone(projectId, activityId);
        if (requiredMilestone?.current_deadline) {
          dates.push(requiredMilestone.current_deadline);
        }

        const firstSuccessorDeadline = this.firstSuccessorDeadline(
          projectId,
          activityId,
        );
        if (firstSuccessorDeadline) {
          dates.push(firstSuccessorDeadline);
        }

        const nextUnplannedOrDonePhaseDate = this.nextUnplannedOrDonePhase(
          projectId,
          activityId,
        );
        if (nextUnplannedOrDonePhaseDate) {
          dates.push(nextUnplannedOrDonePhaseDate);
        }

        return dates.sort()[0];
      };
    },

    currentTags() {
      return (projectId: string, activityId: string): TagTemplate[] => {
        const ids = this.activity(projectId, activityId).tags;

        return ids
          .map((id) => useProjectStore().tag(projectId, id))
          .sort((a, b) => a.title.localeCompare(b.title));
      };
    },

    availableFunctions() {
      return (projectId: string, activityId: string) => {
        const projectStore = useProjectStore();
        const activity = this.activity(projectId, activityId);
        const approversIds =
          activity.approvals.map(({ approver }) => approver.function_id) || [];

        return projectStore
          .functions(projectId)
          .filter(
            ({ id }) =>
              id !== activity.responsible.function_id &&
              !activity.watchers.includes(id) &&
              !approversIds.includes(id),
          );
      };
    },
  },

  actions: {
    writeActivityTags(
      projectId: string,
      activityId: string,
      options: { add?: string[]; remove?: string[] },
    ) {
      if (!this.hasActivity(projectId, activityId)) {
        return;
      }

      const activity = this.activity(projectId, activityId);
      const { add, remove } = options;

      if (add) {
        add.forEach((tagId) => {
          if (!activity.tags.includes(tagId)) {
            activity.tags.push(tagId);
          }
        });
      }

      if (remove) {
        activity.tags = activity.tags.filter(
          (tagId) => !remove.includes(tagId),
        );
      }
    },

    async create(projectId: string, payload: ActivityCreateRequest) {
      const response = await ProjectsService.addPhaseActivity(
        projectId,
        payload,
      );

      this.write(projectId, response);
      if (payload.company_activity_id) {
        await useProjectStore().loadProject(projectId);
      }

      // Returned so callers that need the new activity's id can use it — project
      // mail (WI #5643) links the created activity back to the mail it came from.
      // Pre-existing callers ignore the return value.
      return response;
    },

    async update(
      projectId: string,
      activityId: string,
      payload: ActivityUpdate,
    ) {
      const errors =
        useActivityProjectRulesStore().validateUpdateActivityRequest(
          projectId,
          activityId,
          payload,
        );

      if (errors) {
        throw errors;
      }

      return ProjectsService.updateProjectsActivity(
        projectId,
        activityId,
        payload,
      ).then((activity) => {
        const activityRef = this.write(projectId, activity);
        useProjectStore().writeActivitySummary(projectId, activity);
        if (activity.state === 'underway') {
          const phase = useProjectStore().phase(projectId, activity.phase);
          if (phase.state === 'planned') {
            phase.state = 'underway';
            phase.final_milestone.state = 'underway';
            phase.final_milestone.status = activity.status;
          }
        }

        return activityRef;
      });
    },

    async updateActivities(
      projectId: string,
      payload: ActivitiesUpdateRequest,
    ) {
      const activities = await ProjectsService.updateProjectActivities(
        projectId,
        payload,
      );
      activities.forEach((activity) => {
        this.write(projectId, activity);
      });
      await useProjectStore().loadProject(projectId);
    },

    async duplicate(
      projectId: string,
      payload: ActivityProjectDuplicateRequest,
    ) {
      await ProjectsService.duplicateProjectActivities(projectId, payload);
      const projectStore = useProjectStore();
      await projectStore.loadProject(projectId);
    },

    async remove(projectId: string, activityId: string) {
      await ProjectsService.deleteProjectsActivity(projectId, activityId);
      useChecklistStore().removeActivityChecklist(projectId, activityId);
      await useProjectStore().loadProject(projectId);
    },

    async removeActivities(projectId: string, activityIds: string[]) {
      await ProjectsService.deleteProjectActivities(projectId, {
        activity_ids: activityIds,
      });
      activityIds.forEach((activityId) =>
        useChecklistStore().removeActivityChecklist(projectId, activityId),
      );
      await useProjectStore().loadProject(projectId);
    },

    getProject(projectId: string) {
      if (!this.projects[projectId]) {
        this.projects[projectId] = [];
      }

      return this.projects[projectId];
    },

    write(projectId: string, activity: ActivityDetails) {
      const projectStore = useProjectStore();
      const project = this.getProject(projectId);
      const oldActivity = project.find((act) => act.id === activity.id);
      if (oldActivity) {
        Object.assign(oldActivity, activity);
      } else {
        project.push(activity);
      }
      projectStore.writeActivitySummary(projectId, activitySummaryOf(activity));

      return this.activity(projectId, activity.id);
    },

    writeCriterion(
      projectId: string,
      activityId: string,
      payload: CompletionCriterionDetails,
    ) {
      const activity = this.activity(projectId, activityId);
      const criterion = activity.completion_criteria.find(
        ({ id }) => id === payload.id,
      );

      if (criterion) {
        Object.assign(criterion, payload);
        if (payload.state === 'underway') {
          this.updateActivityAndPhaseStateToUnderway(projectId, activity);
        }
      } else {
        activity.completion_criteria.push(payload);
      }

      return this.criterion(projectId, activityId, payload.id);
    },

    writeAction(projectId: string, activityId: string, payload: ActionDetails) {
      const activity = this.activity(projectId, activityId);
      const action = activity.actions.find(({ id }) => id === payload.id);

      if (action) {
        Object.assign(action, payload);
        if (payload.state === 'underway') {
          this.updateActivityAndPhaseStateToUnderway(projectId, activity);
        }
      } else {
        activity.actions.push(payload);
      }

      return this.action(projectId, activityId, payload.id);
    },

    updateActivityAndPhaseStateToUnderway(
      projectId: string,
      activity: ActivityDetails,
    ) {
      if (activity.state === 'planned') {
        activity.state = 'underway';
        const phase = useProjectStore().phase(projectId, activity.phase);
        if (phase.state === 'planned') {
          phase.state = 'underway';
          phase.final_milestone.state = 'underway';
        }
      }
    },

    async loadActivity(projectId: string, activityId: string) {
      return ProjectsService.getProjectsActivity(projectId, activityId).then(
        (activity) => {
          this.write(projectId, activity);

          return this.activity(projectId, activityId);
        },
      );
    },

    /**
     * Refetches an activity the AI assistant just saved, for the map, the
     * list and the kanban board (WI #5767). Its details are written only
     * when they are cached already: a batch of new Eckpunkte must not fill
     * the cache that `reloadCachedActivities` reloads one by one.
     */
    async refreshActivity(projectId: string, activityId: string) {
      const activity = await ProjectsService.getProjectsActivity(
        projectId,
        activityId,
      );
      if (this.hasActivity(projectId, activityId)) {
        this.write(projectId, activity);
      } else {
        useProjectStore().writeActivitySummary(
          projectId,
          activitySummaryOf(activity),
        );
      }
    },

    async reloadCachedActivities(projectId: string) {
      const activityIds = (this.projects[projectId] ?? []).map(({ id }) => id);

      await Promise.all(
        activityIds.map((activityId) =>
          this.loadActivity(projectId, activityId).catch(() => {
            this.projects[projectId] = this.getProject(projectId).filter(
              ({ id }) => id !== activityId,
            );
          }),
        ),
      );
    },

    async hasActivityAccess(projectId: string, activityId: string) {
      try {
        await ProjectsService.getProjectsActivity(projectId, activityId);

        return true;
      } catch {
        return false;
      }
    },

    async toggleFavorite(projectId: string, activityId: string) {
      await ProjectsService.toggleFavorite(projectId, activityId);
      await this.loadActivity(projectId, activityId);
    },

    async updateActivitiesFavorite(
      projectId: string,
      payload: ActivitiesFavoriteUpdateRequest,
    ) {
      await ProjectsService.updateActivitiesFavorite(projectId, payload);

      const projectStore = useProjectStore();
      payload.activity_ids.forEach((activityId) => {
        projectStore.writeActivitySummary(projectId, {
          id: activityId,
          is_favorite: payload.favorite,
        } as ActivitySummary);
      });
    },

    async updatePosition(
      projectId: string,
      activityId: string,
      payload: ActivityUpdate,
    ) {
      const activity = await ProjectsService.updateProjectsActivity(
        projectId,
        activityId,
        payload,
      );
      const projectStore = useProjectStore();

      if (payload.force_update_deadline) {
        await projectStore.loadProject(projectId);
      } else {
        const activitySummary = projectStore.activitySummary(
          projectId,
          activityId,
        );
        activitySummary.current_deadline = activity.current_deadline;
        // a non-force drag may change the phase too (an activity whose
        // milestone connection is violated is no longer clamped to it); the
        // map derives the activity's phase — and with it whether
        // the connection is violated — from this summary
        activitySummary.phase = activity.phase;

        if (activitySummary.state === 'underway' && payload.phase) {
          const phaseSummary = useProjectStore().phase(
            projectId,
            payload.phase,
          );
          if (
            phaseSummary.state === 'planned' ||
            phaseSummary.state === 'unplanned'
          ) {
            phaseSummary.state = 'underway';
          }
        }
      }
      this.write(projectId, activity);
    },

    async updatePositions(projectId: string, payload: ActivitiesUpdateRequest) {
      const activities = await ProjectsService.updateProjectActivities(
        projectId,
        payload,
      );
      activities.forEach((activity) => {
        this.write(projectId, activity);
      });
      await useProjectStore().loadProject(projectId);
    },

    async updateWorkstream(
      projectId: string,
      activityId: string,
      newWorkstreamId: string,
    ) {
      const activity = await ProjectsService.updateProjectsActivity(
        projectId,
        activityId,
        { workstream: newWorkstreamId },
      );
      this.write(projectId, activity);
    },

    async loadLinks(projectId: string, activityId: string) {
      const links = await ProjectsService.getProjectsActivityLinks(
        projectId,
        activityId,
      );
      const activity = this.activity(projectId, activityId);
      activity.links = links.map((link) => link.id);
      this.write(projectId, activity);
    },

    async loadWatchers(projectId: string, activityId: string) {
      const watchers = await ProjectsService.getProjectsActivityWatchers(
        projectId,
        activityId,
      );
      const activity = this.activity(projectId, activityId);
      activity.watchers = watchers.map(({ id }) => id);
      this.write(projectId, activity);
    },

    async loadApprovals(projectId: string, activityId: string) {
      const approvals = await ProjectsService.getProjectsActivityApprovers(
        projectId,
        activityId,
      );
      const activity = this.activity(projectId, activityId);
      activity.approvals = approvals;
      this.write(projectId, activity);
    },

    async removeWatcher(
      projectId: string,
      activityId: string,
      watcherId: string,
    ) {
      const errors =
        useActivityProjectRulesStore().validateRemoveWatcherRequest(
          projectId,
          activityId,
          watcherId,
        );

      if (errors) {
        throw errors;
      }

      await ProjectsService.removeWatcher(projectId, activityId, watcherId);
      const activity = this.activity(projectId, activityId);

      const watcherIndex = activity.watchers.findIndex(
        (watcher) => watcher === watcherId,
      );
      if (!watcherIndex && watcherIndex !== 0) {
        throw new Error(`No watcher at index ${watcherIndex} found`);
      }

      if (watcherIndex > -1) {
        activity.watchers.splice(watcherIndex, 1);
      }
    },

    async addWatcher(
      projectId: string,
      activityId: string,
      data: AddWatcherRequest,
    ) {
      const errors = useActivityProjectRulesStore().validateAddWatcherRequest(
        projectId,
        activityId,
        data.watcher_id,
      );
      if (errors) {
        throw errors;
      }

      return ProjectsService.addWatcher(projectId, activityId, data).then(
        () => {
          const activity = this.activity(projectId, activityId);
          const projectStore = useProjectStore();
          if (!activity.watchers) {
            activity.watchers = [];
          }
          const watcher = projectStore.projectFunction(
            projectId,
            data.watcher_id,
          );
          if (!watcher) {
            return;
          }
          activity.watchers.push(watcher.id);
        },
      );
    },

    async addApproval(
      projectId: string,
      activityId: string,
      data: ApprovalCreate,
    ) {
      const errors =
        data.approver.function_id &&
        useActivityProjectRulesStore().validateAddApproverRequest(
          projectId,
          activityId,
          data.approver.function_id,
        );
      if (errors) {
        throw errors;
      }

      const approval = await ProjectsService.addApproval(
        projectId,
        activityId,
        data,
      );

      const activity = this.activity(projectId, activityId);

      if (!activity.approvals) {
        activity.approvals = [];
      }

      activity.approvals.push(approval);
      this.write(projectId, activity);
    },

    async removeApprover(
      projectId: string,
      activityId: string,
      approvalId: string,
    ) {
      const errors =
        useActivityProjectRulesStore().validateRemoveApproverRequest(
          projectId,
          activityId,
          approvalId,
        );
      if (errors) {
        throw errors;
      }

      await ProjectsService.removeActivityApprover(
        projectId,
        activityId,
        approvalId,
      );
      const activity = this.activity(projectId, activityId);
      const index = activity.approvals.findIndex(({ id }) => id === approvalId);
      if (!index && index !== 0) {
        throw new Error(`No approver at index ${index} found`);
      }

      if (index > -1) {
        activity.approvals.splice(index, 1);
      }
      this.write(projectId, activity);
    },

    async updateApprover(
      projectId: string,
      activityId: string,
      approvalId: string,
      function_id?: string,
      user_id?: string | null,
      state?: 'pending' | 'approved' | 'rejected',
    ) {
      const newApproval = await ProjectsService.updateActivityApprover(
        projectId,
        activityId,
        approvalId,
        {
          function_id,
          user_id,
          state,
        },
      );
      const activity = this.activity(projectId, activityId);
      const oldApproval = activity.approvals.find(
        ({ id }) => id === approvalId,
      );
      if (!oldApproval) {
        throw new Error(`No approver found`);
      }
      Object.assign(oldApproval, newApproval);
      this.write(projectId, activity);
    },

    async updateApproverState(
      projectId: string,
      activityId: string,
      approvalId: string,
      state?: 'pending' | 'approved' | 'rejected',
    ) {
      const newApproval = await ProjectsService.updateActivityApprover(
        projectId,
        activityId,
        approvalId,
        {
          state,
        },
      );
      const activity = this.activity(projectId, activityId);
      const oldApproval = activity.approvals.find(
        ({ id }) => id === approvalId,
      );
      if (!oldApproval) {
        throw new Error(`No approver found`);
      }
      Object.assign(oldApproval, newApproval);
    },

    async addPredecessor(
      projectId: string,
      activityId: string,
      predecessorId: string,
    ) {
      const errors =
        useActivityProjectRulesStore().validateAddPredecessorToActivityRequest(
          projectId,
          predecessorId,
          activityId,
        );
      if (errors) {
        throw errors;
      }
      const predecessor = await ProjectsService.activityAddPredecessor(
        projectId,
        activityId,
        {
          predecessor_id: predecessorId,
        },
      );

      if (this.hasActivity(projectId, activityId)) {
        const activity = this.activity(projectId, activityId);
        activity.predecessors.push(predecessor.id);
      }
      const summary = useProjectStore().activitySummary(projectId, activityId);
      summary.predecessors.push(predecessor.id);
    },

    async removePredecessor(
      projectId: string,
      activityId: string,
      predecessorId: string,
    ) {
      const errors =
        useActivityProjectRulesStore().validateRemovePredecessorFromActivityRequest(
          projectId,
          activityId,
          predecessorId,
        );
      if (errors) {
        throw errors;
      }
      await ProjectsService.activityRemovePredecessor(
        projectId,
        activityId,
        predecessorId,
      );
      if (this.hasActivity(projectId, activityId)) {
        const activity = this.activity(projectId, activityId);
        activity.predecessors = activity.predecessors.filter(
          (id) => id !== predecessorId,
        );
        activity.documents.forEach((doc) => {
          if (doc.input === predecessorId) {
            doc.input = null;
          }
        });
      }
      const summary = useProjectStore().activitySummary(projectId, activityId);
      summary.predecessors = summary.predecessors.filter(
        (id) => id !== predecessorId,
      );
    },

    async loadSuccessors(projectId: string, activityId: string) {
      const successors = await ProjectsService.getProjectsActivitySuccessors(
        projectId,
        activityId,
      );

      const activity = this.activity(projectId, activityId);
      activity.successors = successors.map(({ id }) => id);
    },

    async loadPredecessors(projectId: string, activityId: string) {
      const predecessors =
        await ProjectsService.getProjectsActivityPredecessors(
          projectId,
          activityId,
        );

      const activity = this.activity(projectId, activityId);
      activity.predecessors = predecessors.map(({ id }) => id);
    },

    async loadDocuments(projectId: string, activityId: string) {
      const documents = await ProjectsService.getProjectActivityDocuments(
        projectId,
        activityId,
      );
      const activity = this.activity(projectId, activityId);
      activity.documents = documents;
    },

    async loadCompletionCriteria(projectId: string, activityId: string) {
      const completionCriteria =
        await ProjectsService.activityGetCompletionCriteria(
          projectId,
          activityId,
        );
      const activity = this.activity(projectId, activityId);
      activity.completion_criteria = completionCriteria;

      return activity.completion_criteria;
    },

    async loadActions(projectId: string, activityId: string) {
      const actions = await ProjectsService.activityGetActions(
        projectId,
        activityId,
      );
      const activity = this.activity(projectId, activityId);
      activity.actions = actions;
    },

    async addSuccessor(
      projectId: string,
      activityId: string,
      successorId: string,
    ) {
      const successor = await ProjectsService.activityAddSuccessor(
        projectId,
        activityId,
        {
          successor_id: successorId,
        },
      );

      const activity = this.activity(projectId, activityId);
      activity.successors.push(successor.id);
    },

    async removeSuccessor(
      projectId: string,
      activityId: string,
      successorId: string,
    ) {
      await ProjectsService.activityRemoveSuccessor(
        projectId,
        activityId,
        successorId,
      );
      const activity = this.activity(projectId, activityId);
      const index = activity.successors.findIndex((id) => id === successorId);
      if (index > -1) {
        activity.successors.splice(index, 1);
      }

      const activitySummary = useProjectStore().activitySummary(
        projectId,
        successorId,
      );
      const index2 = activitySummary.predecessors.findIndex(
        (id) => id === activityId,
      );
      if (index2 > -1) {
        activity.predecessors.splice(index, 1);
      }
    },

    async addTag(
      projectId: string,
      activityId: string,
      payload: ActivityTagCreate,
    ) {
      const errors =
        useActivityProjectRulesStore().validateAddTagToActivityRequest(
          projectId,
          activityId,
          payload,
        );
      if (errors) {
        throw errors;
      }
      const projectStore = useProjectStore();
      const { tag } = await ProjectsService.activityCreateTag(
        projectId,
        activityId,
        payload,
      );
      await projectStore.fetchTags(projectId);
      const activity = this.activity(projectId, activityId);
      activity.tags.push(tag.id);
      projectStore.writeActivitySummary(projectId, activity);
    },

    async removeTag(projectId: string, activityId: string, tagId: string) {
      const errors =
        useActivityProjectRulesStore().validateRemoveTagFromActivityRequest(
          projectId,
          activityId,
          tagId,
        );
      if (errors) {
        throw errors;
      }
      const { removed_from_project } = await ProjectsService.activityDeleteTag(
        projectId,
        activityId,
        tagId,
      );
      const activity = this.activity(projectId, activityId);
      const tagIndex = activity.tags.findIndex((id) => id === tagId);
      if (tagIndex > -1) {
        activity.tags.splice(tagIndex, 1);
      }
      if (!removed_from_project) {
        return;
      }
      const projectStore = useProjectStore();
      projectStore.writeActivitySummary(projectId, activity);
      await projectStore.fetchTags(projectId);
    },

    async updateActivitiesTags(
      projectId: string,
      payload: ActivitiesTagsUpdateRequest,
    ) {
      const projectStore = useProjectStore();
      const { tags: newTags } = await ProjectsService.updateActivitiesTags(
        projectId,
        payload,
      );
      const { add, remove, activity_ids } = payload;

      // Fetch updated tags from project (new tags may have been created via add_new)
      await projectStore.fetchTags(projectId);

      // Optimistically update store(s)

      // Get IDs of newly created tags
      const newTagIds = newTags.map((tag) => tag.id);
      // Combine add tags with newly created tags
      const allTagsToAdd = [...(add || []), ...newTagIds];

      // Update each activity's tags
      activity_ids.forEach((activityId) => {
        // Update detailed activity if loaded in store
        this.writeActivityTags(projectId, activityId, {
          add: allTagsToAdd,
          remove,
        });

        //  Update activity summary in project store (for ioStore reactivity)
        projectStore.writeActivityTags(projectId, activityId, {
          add: allTagsToAdd,
          remove,
        });
      });
    },

    async loadActivityInputs(
      projectId: string,
      activityId: string,
      documentId?: string,
    ) {
      return ProjectsService.getActivityAvailableInputDocuments(
        projectId,
        activityId,
        documentId,
      ).then((inputs) => inputs);
    },

    async removeDocumentFromActivity(
      projectId: string,
      activityId: string,
      documentId: string,
    ) {
      await ProjectsService.removeActivityDocument(
        projectId,
        activityId,
        documentId,
      );
      const activity = this.activity(projectId, activityId);
      const documents = activity.documents.filter(
        ({ id }) => id !== documentId,
      );
      Object.assign(activity, {
        documents,
      });
      await useProjectStore().fetchDocuments(projectId);
    },

    async addActivityDocument(
      projectId: string,
      activityId: string,
      data: ActivityDocument,
    ) {
      const document = await ProjectsService.addActivityDocument(
        projectId,
        activityId,
        data,
      );
      await useProjectStore().fetchDocuments(projectId);
      this.activity(projectId, activityId).documents.push(document);
    },

    async updateActivityDocument(
      projectId: string,
      activityId: string,
      docId: string,
      data: UpdateActivityDocumentRequest,
    ) {
      const errors =
        useActivityProjectRulesStore().validateUpdateActivityDocumentRequest(
          projectId,
          activityId,
          data,
          docId,
        );

      if (errors) {
        throw errors;
      }

      return ProjectsService.updateActivityDocument(
        projectId,
        activityId,
        docId,
        data,
      ).then((document) => {
        this.writeDocument(projectId, activityId, document);
      });
    },

    writeDocument(
      projectId: string,
      activityId: string,
      payload: ActivityDocument,
    ) {
      const activity = this.activity(projectId, activityId);
      const doc = activity.documents.find(({ id }) => id === payload.id);
      if (doc) {
        Object.assign(doc, payload);
      } else {
        activity.documents.push(payload);
      }
    },

    async removeCompletionCriterion(
      projectId: string,
      activityId: string,
      criterionId: string,
    ) {
      const errors = useActivityProjectRulesStore().validateRemoveCCRequest(
        projectId,
        activityId,
        criterionId,
      );
      if (errors) {
        throw errors;
      }

      await ProjectsService.activityRemoveCompletionCriterion(
        projectId,
        activityId,
        criterionId,
      );
      // From the map's summary too: going back to the map reloads nothing
      useProjectStore().removeTaskLocally(
        projectId,
        activityId,
        'completion_criteria',
        criterionId,
      );
    },

    async convertCompletionCriterionToActivity(
      projectId: string,
      activityId: string,
      criterionId: string,
    ) {
      const response =
        await ProjectsService.convertCompletionCriterionToActivity(
          projectId,
          activityId,
          criterionId,
        );
      this.write(projectId, response);
      useProjectStore().removeTaskLocally(
        projectId,
        activityId,
        'completion_criteria',
        criterionId,
      );

      return response;
    },

    async removeAction(
      projectId: string,
      activityId: string,
      actionId: string,
    ) {
      const errors = useActivityProjectRulesStore().validateRemoveActionRequest(
        projectId,
        activityId,
        actionId,
      );
      if (errors) {
        throw errors;
      }

      await ProjectsService.activityRemoveAction(
        projectId,
        activityId,
        actionId,
      );
      // From the map's summary too: going back to the map reloads nothing
      useProjectStore().removeTaskLocally(
        projectId,
        activityId,
        'actions',
        actionId,
      );
    },

    async convertActionToActivity(
      projectId: string,
      activityId: string,
      actionId: string,
    ) {
      const response = await ProjectsService.convertActionToActivity(
        projectId,
        activityId,
        actionId,
      );
      this.write(projectId, response);
      useProjectStore().removeTaskLocally(
        projectId,
        activityId,
        'actions',
        actionId,
      );

      return response;
    },

    async getApprovals(projectId: string, activityId: string) {
      return await ProjectsService.getProjectsActivityApprovers(
        projectId,
        activityId,
      );
    },

    async requestApproval(
      projectId: string,
      activityId: string,
      approvalId: string,
    ) {
      await ProjectsService.requestActivityApproval(
        projectId,
        activityId,
        approvalId,
      );
    },

    async addLinkToActivity(
      projectId: string,
      activityId: string,
      payload: AddLinkRequest,
    ) {
      await ProjectsService.addActivityLink(projectId, activityId, payload);
      await useProjectStore().fetchLinks(projectId);
      this.activity(projectId, activityId).links.push(payload.link_id);
    },

    async removeLinkFromActivity(
      projectId: string,
      activityId: string,
      linkId: string,
    ) {
      const errors =
        useActivityProjectRulesStore().validateRemoveLinkFromActivityRequest(
          projectId,
          activityId,
          linkId,
        );
      if (errors) {
        throw errors;
      }
      await ProjectsService.removeActivityLink(projectId, activityId, linkId);
      const activity = this.activity(projectId, activityId);
      const linkIndex = activity.links.findIndex((link) => link === linkId);
      if (linkIndex > -1) {
        activity.links.splice(linkIndex, 1);
      }
      await useProjectStore().fetchLinks(projectId);
    },

    async updateActivityFromLibrary(
      projectId: string,
      activityId: string,
      libraryActivityId: string,
    ): Promise<string> {
      const newActivity: ActivityDetails =
        await ProjectsService.updateActivityFromLibrary(projectId, activityId, {
          company_activity_id: libraryActivityId,
        });

      const projectStore = useProjectStore();
      await projectStore.loadProject(projectId);

      return newActivity.id;
    },

    async addActivityComment(
      projectId: string,
      activityId: string,
      payload: CommentCreateRequest,
    ): Promise<Comment> {
      const comment = await ProjectsService.addActivityComment(
        projectId,
        activityId,
        payload,
      );
      // Not `this.activity()`: it throws for an activity whose details were never loaded
      // (a mail dropped on the map, WI #5719), and the comment is already saved by then.
      this.projects[projectId]
        ?.find(({ id }) => id === activityId)
        ?.comments.unshift(comment);

      return comment;
    },

    async updateActivityComment(
      projectId: string,
      activityId: string,
      commentId: string,
      payload: CommentUpdateRequest,
    ): Promise<Comment> {
      const updatedComment = await ProjectsService.updateActivityComment(
        projectId,
        activityId,
        commentId,
        payload,
      );
      const activity = this.activity(projectId, activityId);
      const comment = activity.comments.find(({ id }) => id === commentId);
      if (comment) {
        Object.assign(comment, updatedComment);
      }

      return updatedComment;
    },

    async removeActivityComment(
      projectId: string,
      activityId: string,
      commentId: string,
    ): Promise<void> {
      await ProjectsService.removeActivityComment(
        projectId,
        activityId,
        commentId,
      );
      const activity = this.activity(projectId, activityId);
      const commentIndex = activity.comments.findIndex(
        ({ id }) => id === commentId,
      );
      if (commentIndex > -1) {
        activity.comments.splice(commentIndex, 1);
      }
    },

    async addActionComment(
      projectId: string,
      activityId: string,
      actionId: string,
      payload: CommentCreateRequest,
    ): Promise<Comment> {
      const comment = await ProjectsService.addActionComment(
        projectId,
        activityId,
        actionId,
        payload,
      );
      const action = this.action(projectId, activityId, actionId);
      action.comments.unshift(comment);

      return comment;
    },

    async updateActionComment(
      projectId: string,
      activityId: string,
      actionId: string,
      commentId: string,
      payload: CommentUpdateRequest,
    ): Promise<Comment> {
      const updatedComment = await ProjectsService.updateActionComment(
        projectId,
        activityId,
        actionId,
        commentId,
        payload,
      );
      const action = this.action(projectId, activityId, actionId);
      const comment = action.comments.find(({ id }) => id === commentId);
      if (comment) {
        Object.assign(comment, updatedComment);
      }

      return updatedComment;
    },

    async removeActionComment(
      projectId: string,
      activityId: string,
      actionId: string,
      commentId: string,
    ): Promise<void> {
      await ProjectsService.removeActionComment(
        projectId,
        activityId,
        actionId,
        commentId,
      );
      const action = this.action(projectId, activityId, actionId);
      const commentIndex = action.comments.findIndex(
        ({ id }) => id === commentId,
      );
      if (commentIndex > -1) {
        action.comments.splice(commentIndex, 1);
      }
    },

    async addCompletionCriterionComment(
      projectId: string,
      activityId: string,
      criterionId: string,
      payload: CommentCreateRequest,
    ): Promise<Comment> {
      const comment = await ProjectsService.addCompletionCriterionComment(
        projectId,
        activityId,
        criterionId,
        payload,
      );
      const criterion = this.criterion(projectId, activityId, criterionId);
      criterion.comments.unshift(comment);

      return comment;
    },

    async updateCompletionCriterionComment(
      projectId: string,
      activityId: string,
      criterionId: string,
      commentId: string,
      payload: CommentUpdateRequest,
    ): Promise<Comment> {
      const updatedComment =
        await ProjectsService.updateCompletionCriterionComment(
          projectId,
          activityId,
          criterionId,
          commentId,
          payload,
        );
      const criterion = this.criterion(projectId, activityId, criterionId);
      const comment = criterion.comments.find(({ id }) => id === commentId);
      if (comment) {
        Object.assign(comment, updatedComment);
      }

      return updatedComment;
    },

    async removeCompletionCriterionComment(
      projectId: string,
      activityId: string,
      criterionId: string,
      commentId: string,
    ): Promise<void> {
      await ProjectsService.removeCompletionCriterionComment(
        projectId,
        activityId,
        criterionId,
        commentId,
      );
      const criterion = this.criterion(projectId, activityId, criterionId);
      const commentIndex = criterion.comments.findIndex(
        ({ id }) => id === commentId,
      );
      if (commentIndex > -1) {
        criterion.comments.splice(commentIndex, 1);
      }
    },
  },
});
